import { useCallback, useEffect, useRef, useState } from 'react';
import { useReverification, useUser } from '@clerk/react';
import type { PhoneNumberResource } from '@clerk/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { advisorAccountActivationApi, advisorAccountActivationKeys } from '@client/api/waterlily';
import { setAuthToken } from '@client/api/queryClient';
import { setRoleOverride } from '@client/hooks/useRoleSwitch';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import type { API } from '@client/api/types';
import {
  SUPPORTED_COUNTRIES,
  type SupportedCountryCode,
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
} from '@client/util/clerkAuthHelpers';

export type AdvisorAccountActivationStep =
  | 'loading'
  | 'confirmation'
  | 'phone'
  | 'verify'
  | 'provisioning'
  | 'blocked'
  | 'error';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const clerkErrors = 'errors' in error ? error.errors : undefined;
    if (Array.isArray(clerkErrors) && clerkErrors.length > 0) {
      const first = clerkErrors[0] as { longMessage?: string; message?: string };
      return clerkMessage(first) ?? 'We could not complete that step. Please try again.';
    }

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
  }

  return 'We could not complete that step. Please try again.';
}

export function useAdvisorAccountActivation({
  startAfterConfirmation = false,
  initialPreflight = null,
  emailOnlyOverride = false,
}: {
  startAfterConfirmation?: boolean;
  initialPreflight?: API.AdvisorAccountActivationPreflight | null;
  emailOnlyOverride?: boolean;
} = {}) {
  const { user, isLoaded } = useUser();
  const clerkUserId = user?.id;
  const { getClerkToken } = useClerkClientAuth();
  const [step, setStep] = useState<AdvisorAccountActivationStep>(() =>
    initialPreflight
      ? initialPreflight.status === 'blocked'
        ? 'blocked'
        : 'confirmation'
      : 'loading',
  );
  const [preflight, setPreflight] = useState<API.AdvisorAccountActivationPreflight | null>(
    initialPreflight,
  );
  const [countryCode, setCountryCode] = useState<SupportedCountryCode>(
    SUPPORTED_COUNTRIES[0]?.code ?? '+1',
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activationSubmitted, setActivationSubmitted] = useState(false);
  const [failedOperation, setFailedOperation] = useState<'preflight' | 'activation'>('preflight');
  const phoneResourceRef = useRef<PhoneNumberResource | null>(null);
  const hasStartedAfterConfirmationRef = useRef(false);
  const overrideConnection = emailOnlyOverride ? ('email' as const) : undefined;

  const preflightQuery = useQuery({
    queryKey: advisorAccountActivationKeys.preflight(overrideConnection),
    queryFn: () => advisorAccountActivationApi.getPreflight({ overrideConnection }),
    enabled: !initialPreflight && isLoaded && !!clerkUserId,
    retry: false,
  });
  const activationMutation = useMutation({
    mutationFn: () => advisorAccountActivationApi.activate({ overrideConnection }),
  });
  const { refetch: refetchPreflight } = preflightQuery;
  const { mutateAsync: activateAccount } = activationMutation;

  const createPhoneNumber = useReverification((e164: string) => {
    if (!user) throw new Error('Your signed-in account is not available. Please refresh the page.');
    return user.createPhoneNumber({ phoneNumber: e164 });
  });

  const applyPreflight = useCallback(
    (result: API.AdvisorAccountActivationPreflight) => {
      setPreflight(result);

      if (result.status === 'already-advisor' && result.advisorId) {
        if (!clerkUserId) {
          setError('Your signed-in account is not available. Please refresh the page.');
          setFailedOperation('preflight');
          setStep('error');
          return;
        }

        setRoleOverride('advisor', clerkUserId);
        globalThis.location.assign(`/advisors/${result.advisorId}`);
        return;
      }

      setStep(result.status === 'blocked' ? 'blocked' : 'confirmation');
    },
    [clerkUserId],
  );

  useEffect(() => {
    if (initialPreflight || !preflightQuery.isSuccess) return;
    applyPreflight(preflightQuery.data);
  }, [applyPreflight, initialPreflight, preflightQuery.data, preflightQuery.isSuccess]);

  useEffect(() => {
    if (initialPreflight || !preflightQuery.isError) return;
    setError(getErrorMessage(preflightQuery.error));
    setFailedOperation('preflight');
    setStep('error');
  }, [initialPreflight, preflightQuery.error, preflightQuery.isError]);

  const loadPreflight = useCallback(async () => {
    if (!isLoaded || !clerkUserId) return;

    setStep('loading');
    setError(null);
    const result = await refetchPreflight();
    if (result.data) {
      applyPreflight(result.data);
      return;
    }

    if (result.error) {
      setError(getErrorMessage(result.error));
      setFailedOperation('preflight');
      setStep('error');
    }
  }, [applyPreflight, clerkUserId, isLoaded, refetchPreflight]);

  const activate = useCallback(async () => {
    if (!user) return;

    setStep('provisioning');
    setIsSubmitting(true);
    setError(null);
    setActivationSubmitted(true);
    try {
      const result = await activateAccount();
      if (!result.requiresTokenRefresh) {
        throw new Error('Your advisor session could not be refreshed. Please try again.');
      }

      await user.reload();
      const freshToken = await getClerkToken({ skipCache: true });
      if (!freshToken) {
        throw new Error('Your advisor session could not be refreshed. Please try again.');
      }

      setAuthToken(freshToken);
      setRoleOverride('advisor', user.id);
      globalThis.location.assign(result.onboardingPath);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setFailedOperation('activation');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [activateAccount, getClerkToken, user]);

  const continueFromConfirmation = useCallback(async () => {
    if (!preflight) return;
    if (preflight.phoneVerificationRequired) {
      setError(null);
      setStep('phone');
      return;
    }
    await activate();
  }, [activate, preflight]);

  useEffect(() => {
    if (
      !startAfterConfirmation ||
      step !== 'confirmation' ||
      hasStartedAfterConfirmationRef.current
    ) {
      return;
    }

    hasStartedAfterConfirmationRef.current = true;
    void continueFromConfirmation();
  }, [continueFromConfirmation, startAfterConfirmation, step]);

  const updatePhoneNumber = useCallback(
    (value: string) => {
      setPhoneNumber(formatByCountry(stripDigits(value), countryCode));
      setError(null);
    },
    [countryCode],
  );

  const updateCountryCode = useCallback((value: SupportedCountryCode) => {
    setCountryCode(value);
    setPhoneNumber('');
    setError(null);
  }, []);

  const sendPhoneCode = useCallback(async () => {
    if (!user) return;

    const digits = stripDigits(phoneNumber);
    const validationError = validateForCountry(digits, countryCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    const e164 = `${countryCode}${digits}`;
    setIsSubmitting(true);
    setError(null);
    try {
      let phoneResource = user.phoneNumbers.find(phone => phone.phoneNumber === e164) ?? null;

      if (phoneResource?.verification.status === 'verified') {
        await activate();
        return;
      }

      if (!phoneResource) {
        phoneResource = (await createPhoneNumber(e164)) ?? null;
        await user.reload();
      }

      if (!phoneResource) {
        throw new Error('We could not add this phone number. Please try again.');
      }

      phoneResourceRef.current = phoneResource;
      await phoneResource.prepareVerification();
      setPhoneCode('');
      setStep('verify');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, [activate, countryCode, createPhoneNumber, phoneNumber, user]);

  const verifyPhoneCode = useCallback(async () => {
    const phoneResource = phoneResourceRef.current;
    if (!phoneResource || !user) {
      setError('Your phone verification expired. Please enter your number again.');
      setStep('phone');
      return;
    }

    if (phoneCode.length !== 6) {
      setError('Enter the 6-digit code sent to your phone.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const verifiedPhone = await phoneResource.attemptVerification({ code: phoneCode });
      if (verifiedPhone.verification.status !== 'verified') {
        throw new Error('That code could not be verified. Please try again.');
      }
      await user.reload();
      await activate();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, [activate, phoneCode, user]);

  const resendPhoneCode = useCallback(async () => {
    if (!phoneResourceRef.current) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await phoneResourceRef.current.prepareVerification();
      setPhoneCode('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const returnToPhone = useCallback(() => {
    setPhoneCode('');
    setError(null);
    setStep('phone');
  }, []);

  const retryFailedStep = useCallback(async () => {
    if (failedOperation === 'activation') {
      await activate();
      return;
    }
    await loadPreflight();
  }, [activate, failedOperation, loadPreflight]);

  return {
    step,
    preflight,
    countryCode,
    phoneNumber,
    phoneCode,
    error,
    isSubmitting,
    activationSubmitted,
    retryFailedStep,
    continueFromConfirmation,
    updatePhoneNumber,
    updateCountryCode,
    sendPhoneCode,
    setPhoneCode,
    verifyPhoneCode,
    resendPhoneCode,
    returnToPhone,
  };
}
